import Swal from "sweetalert2";
import * as dayjs from "dayjs";

const DEFAULT_FILTERS = {
  search: "",
  role_id: "",
  days: 7,
  sort: "last_seen_at",
  direction: "desc",
  per_page: 25,
  page: 1,
};

export default {
  namespaced: true,
  state: {
    users: [],
    pagination: { currentPage: 1, lastPage: 1, total: 0 },
    summary: { onlineNow: 0, activeSecondsTotal: 0, totalUsers: 0 },
    trackingStartedAt: null,
    filters: { ...DEFAULT_FILTERS },
    isLoading: false,
    failed: false,
    history: {},
  },
  mutations: {
    SET_STATE: (state, obj) => {
      state[`${obj.key}`] = obj.value;
    },
    SET_FILTER: (state, { key, value }) => {
      state.filters = { ...state.filters, [key]: value };
    },
    RESET_FILTERS: (state) => {
      state.filters = { ...DEFAULT_FILTERS };
    },
    SET_HISTORY: (state, { userId, value }) => {
      state.history = { ...state.history, [userId]: value };
    },
  },
  actions: {
    getUserActivity({ commit, state }) {
      commit("SET_STATE", { key: "isLoading", value: true });
      const vm = this;

      const params = {
        page: state.filters.page,
        per_page: state.filters.per_page,
        sort: state.filters.sort,
        direction: state.filters.direction,
        date_from: dayjs().subtract(state.filters.days, "day").toISOString(),
        date_to: dayjs().toISOString(),
      };

      if (state.filters.search) params.search = state.filters.search;
      if (state.filters.role_id) params.role_id = state.filters.role_id;

      return new Promise((resolve) => {
        return vm.$http.get("user-activity", { params }).then(
          (res) => {
            commit("SET_STATE", { key: "users", value: res.data.data || [] });
            commit("SET_STATE", {
              key: "pagination",
              value: {
                currentPage: res.data.meta?.current_page || 1,
                lastPage: res.data.meta?.last_page || 1,
                total: res.data.meta?.total || 0,
              },
            });
            commit("SET_STATE", {
              key: "trackingStartedAt",
              value: res.data.tracking_started_at || null,
            });
            commit("SET_STATE", {
              key: "summary",
              value: {
                onlineNow: res.data.summary?.online_now || 0,
                activeSecondsTotal: res.data.summary?.active_seconds_total || 0,
                totalUsers: res.data.summary?.total_users || 0,
              },
            });
            commit("SET_STATE", { key: "failed", value: false });
            commit("SET_STATE", { key: "isLoading", value: false });
            resolve(res.data.data);
          },
          (err) => {
            // Clear the previous result set on failure - otherwise the last
            // successful response stays on screen looking as if it matched the
            // newly selected filters.
            commit("SET_STATE", { key: "users", value: [] });
            commit("SET_STATE", {
              key: "pagination",
              value: { currentPage: 1, lastPage: 1, total: 0 },
            });
            commit("SET_STATE", {
              key: "summary",
              value: { onlineNow: 0, activeSecondsTotal: 0, totalUsers: 0 },
            });
            commit("SET_STATE", { key: "failed", value: true });
            commit("SET_STATE", { key: "isLoading", value: false });
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve();
          }
        );
      });
    },

    getLoginHistory({ commit, state }, userId) {
      const existing = state.history[userId];
      if (existing && existing.isLoading) return Promise.resolve();

      commit("SET_HISTORY", {
        userId,
        value: {
          isLoading: true,
          rows: existing?.rows || [],
          total: 0,
          page: 1,
          lastPage: 1,
        },
      });

      const vm = this;

      return new Promise((resolve) => {
        return vm.$http.get(`user-activity/${userId}/logins`).then(
          (res) => {
            commit("SET_HISTORY", {
              userId,
              value: {
                isLoading: false,
                rows: res.data.data || [],
                total: res.data.meta?.total || 0,
                page: res.data.meta?.current_page || 1,
                lastPage: res.data.meta?.last_page || 1,
              },
            });
            resolve(res.data.data);
          },
          (err) => {
            commit("SET_HISTORY", {
              userId,
              value: {
                isLoading: false,
                rows: [],
                total: 0,
                page: 1,
                lastPage: 1,
                failed: true,
              },
            });
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text:
                  err?.data?.error?.message || "Could not load login history.",
                icon: "error",
              });
            }
            resolve();
          }
        );
      });
    },

    loadMoreHistory({ commit, state }, userId) {
      const existing = state.history[userId];
      if (
        !existing ||
        existing.isLoading ||
        existing.page >= existing.lastPage
      ) {
        return Promise.resolve();
      }

      const nextPage = existing.page + 1;
      commit("SET_HISTORY", {
        userId,
        value: { ...existing, isLoading: true },
      });

      const vm = this;

      return new Promise((resolve) => {
        return vm.$http
          .get(`user-activity/${userId}/logins`, { params: { page: nextPage } })
          .then(
            (res) => {
              commit("SET_HISTORY", {
                userId,
                value: {
                  isLoading: false,
                  rows: [...existing.rows, ...(res.data.data || [])],
                  total: res.data.meta?.total || existing.total,
                  page: res.data.meta?.current_page || nextPage,
                  lastPage: res.data.meta?.last_page || existing.lastPage,
                },
              });
              resolve(res.data.data);
            },
            (err) => {
              commit("SET_HISTORY", {
                userId,
                value: { ...existing, isLoading: false },
              });
              if (!err.accessDenied) {
                Swal.fire({
                  title: "Error",
                  text:
                    err?.data?.error?.message ||
                    "Could not load more login history.",
                  icon: "error",
                });
              }
              resolve();
            }
          );
      });
    },
  },
};
